/**
 * Pure view model of the manager statuses (Р9/D8 — SR v2.1 §3.6/§3.7, BR §3.6; T.Y10).
 *
 * Framework-free on purpose — the `unit` Playwright project runs in plain Node, where
 * neither React nor a DOM exists (the same rule `applications-model.ts` states). The
 * DOM half is the «Статус менеджера» block of the profile (`ApplicationProfilePage`)
 * and the register filter (`ApplicationsFilterBar`); both render exactly what the
 * model answers here.
 *
 * The one rule that shapes everything (A9 as amended by D8): the database keeps a
 * JOURNAL and the interface shows ONE value — the active row with the greatest
 * `assigned_at`; no active row at all is the «без статуса» state, not an error.
 */

import type { ApplicationManagerStatusDto, ManagerStatusDto } from '../../api/manager-statuses';
import type { ComboboxOption } from '../../components/combobox-model';

// ---------------------------------------------------------------------------
// The «без статуса» state and the D8 display rule
// ---------------------------------------------------------------------------

/** The literal the contract pins for the «без статуса» bucket (`#3 ?managerStatus=`). */
export const MANAGER_STATUS_NONE = 'none';

/** How the empty state reads — a state, not an error (Р9). */
export const NO_STATUS_LABEL = 'без статуса';

/** The active rows of a journal: `removedAt IS NULL` (the D8 active state). */
export function activeRows(journal: readonly ApplicationManagerStatusDto[] | undefined): ApplicationManagerStatusDto[] {
  return (journal ?? []).filter((row) => row.removedAt === null);
}

/**
 * THE status the UI shows: the active row with the greatest `assignedAt` (D8).
 * The api already leads its answer with the newest active row, so `find` would do —
 * the scan keeps the rule true even for a hand-built or out-of-order array, with the
 * payload order as the deterministic tie-break for equal timestamps.
 */
export function activeManagerStatus(
  journal: readonly ApplicationManagerStatusDto[] | undefined,
): ApplicationManagerStatusDto | null {
  let winner: ApplicationManagerStatusDto | null = null;
  for (const row of activeRows(journal)) {
    if (winner === null || row.assignedAt > winner.assignedAt) winner = row;
  }
  return winner;
}

/** How many assignments have been closed so far — the «история» hint, nothing more. */
export function journalHistoryCount(journal: readonly ApplicationManagerStatusDto[] | undefined): number {
  return (journal ?? []).filter((row) => row.removedAt !== null).length;
}

/** `1 → «история: 1 смена»`, `2 → «…2 смены»`, `5 → «…5 смен»`; `0 → null` (not rendered). */
export function historyCountLabel(count: number): string | null {
  if (!Number.isFinite(count) || count <= 0) return null;
  return `история: ${count} ${ruPlural(count, ['смена', 'смены', 'смен'])}`;
}

/** RU plural forms (the `1 смена / 2 смены / 5 смен` triad, 11–14 folded to «смен»). */
export function ruPlural(count: number, forms: readonly [string, string, string]): string {
  const mod100 = Math.abs(Math.trunc(count)) % 100;
  const mod10 = mod100 % 10;
  if (mod100 >= 11 && mod100 <= 14) return forms[2];
  if (mod10 === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4) return forms[1];
  return forms[2];
}

// ---------------------------------------------------------------------------
// Combobox options
// ---------------------------------------------------------------------------

/** The last option of the register filter — the bucket Р9 made mandatory. */
export const NO_STATUS_OPTION: ComboboxOption = { value: MANAGER_STATUS_NONE, label: 'Без статуса' };

/**
 * The dictionary as the profile block's combobox lists it: ACTIVE rows only (an
 * inactive id must never become a new assignment — #5.6 answers 400), in the api's
 * `sortOrder`. A current status the dictionary no longer knows (deactivated while
 * still assigned) is prepended so the selection keeps reading as a name and never
 * as «nothing selected» — the same rescue the venue filter applies with row names.
 */
export function managerStatusComboboxOptions(
  dictionary: readonly ManagerStatusDto[] | undefined,
  current?: ApplicationManagerStatusDto | null,
): ComboboxOption[] {
  const options: ComboboxOption[] = (dictionary ?? [])
    .filter((entry) => entry.active)
    .map((entry) => ({ value: String(entry.id), label: entry.label }));

  if (current && !options.some((option) => option.value === String(current.statusId))) {
    options.unshift({ value: String(current.statusId), label: current.label });
  }
  return options;
}

/** The register filter's list: the active dictionary, then «Без статуса» last. */
export function managerStatusFilterOptions(
  dictionary: readonly ManagerStatusDto[] | undefined,
): ComboboxOption[] {
  return [...managerStatusComboboxOptions(dictionary), NO_STATUS_OPTION];
}

// ---------------------------------------------------------------------------
// The profile block — what it renders
// ---------------------------------------------------------------------------

/** The render state of the «Статус менеджера» block (the unit suite pins this). */
export interface ManagerStatusBlockModel {
  /** The ONE active status, `null` = «без статуса». */
  active: ApplicationManagerStatusDto | null;
  /** The text the block prints — `NO_STATUS_LABEL` when there is no active row. */
  label: string;
  hasStatus: boolean;
  /** Closed assignments so far (the journal is never deleted). */
  historyCount: number;
  /** `«история: 2 смены»`, or `null` while there is nothing to count. */
  historyLabel: string | null;
  /** The combobox value — `''` is the cleared state, i.e. «без статуса». */
  value: string;
  /** The combobox options (active dictionary, plus the deactivated current). */
  options: ComboboxOption[];
}

/** Everything the block renders, derived in one pure step from #28 + #5.5. */
export function managerStatusBlock(input: {
  journal: readonly ApplicationManagerStatusDto[] | undefined;
  dictionary: readonly ManagerStatusDto[] | undefined;
}): ManagerStatusBlockModel {
  const active = activeManagerStatus(input.journal);
  const historyCount = journalHistoryCount(input.journal);
  return {
    active,
    label: active === null ? NO_STATUS_LABEL : active.label,
    hasStatus: active !== null,
    historyCount,
    historyLabel: historyCountLabel(historyCount),
    value: active === null ? '' : String(active.statusId),
    options: managerStatusComboboxOptions(input.dictionary, active),
  };
}

// ---------------------------------------------------------------------------
// The set / change / remove flow (#5.6 is a REPLACE-SET)
// ---------------------------------------------------------------------------

export interface ManagerStatusPut {
  /** `[]` is the removal — how a row becomes «без статуса». */
  statusIds: number[];
  /** `false` when the value repeats the current status — the api would no-op it. */
  changed: boolean;
}

/**
 * The combobox commit → the #5.6 body. A non-numeric value (the filter's own
 * `none` token, a stray paste) never leaves the browser: the UI offers only the
 * dictionary ids, so anything else reads as «clear» only through the explicit `''`.
 */
export function managerStatusPut(
  value: string,
  current: ApplicationManagerStatusDto | null,
): ManagerStatusPut {
  // `''` (the combobox's clear button) is the removal — and a no-op when there was
  // nothing to remove, so the page does not fire a pointless PUT.
  if (value === '') return { statusIds: [], changed: current !== null };
  const id = Number.parseInt(value, 10);
  if (!Number.isInteger(id) || id < 1) return { statusIds: [], changed: false };
  // The same status again would be a no-op on the api side — the body stays empty
  // so a caller that ignores `changed` still cannot fire a pointless request.
  if (id === (current?.statusId ?? null)) return { statusIds: [], changed: false };
  return { statusIds: [id], changed: true };
}

// ---------------------------------------------------------------------------
// The register filter (#3 `?managerStatusId=` / `?managerStatus=none`)
// ---------------------------------------------------------------------------

/** The two keys the filter owns — the contract's own names, one value per key. */
export const MANAGER_STATUS_PARAM = 'managerStatus';
export const MANAGER_STATUS_ID_PARAM = 'managerStatusId';

/**
 * What the URL says, as a tagged union. The api answers 400 when BOTH keys travel,
 * so the parser folds them — `managerStatusId` wins (the more specific of the two),
 * which can only ever happen on a hand-edited or half-shared link.
 */
export type ManagerStatusFilter = { kind: 'none' } | { kind: 'id'; id: number };

export function parseManagerStatusFilter(params: Record<string, string>): ManagerStatusFilter | undefined {
  const id = parseIntParam(params[MANAGER_STATUS_ID_PARAM]);
  if (id !== undefined) return { kind: 'id', id };
  const none = (params[MANAGER_STATUS_PARAM] ?? '').trim().toLowerCase();
  return none === MANAGER_STATUS_NONE ? { kind: 'none' } : undefined;
}

/** The value the filter combobox prints: `'none'`, the id as text, or `''`. */
export function managerStatusFilterValue(params: Record<string, string>): string {
  const filter = parseManagerStatusFilter(params);
  if (filter === undefined) return '';
  return filter.kind === 'none' ? MANAGER_STATUS_NONE : String(filter.id);
}

/**
 * The URL patch one combobox commit produces: `none` travels under `managerStatus`,
 * an id under `managerStatusId`, and the OTHER key is always cleared — the two must
 * never meet on the wire.
 */
export function managerStatusFilterPatch(
  value: string,
): { [MANAGER_STATUS_PARAM]?: string; [MANAGER_STATUS_ID_PARAM]?: string } {
  const trimmed = value.trim();
  if (trimmed === MANAGER_STATUS_NONE) return { [MANAGER_STATUS_PARAM]: MANAGER_STATUS_NONE, [MANAGER_STATUS_ID_PARAM]: undefined };
  const id = parseIntParam(trimmed);
  if (id !== undefined) return { [MANAGER_STATUS_ID_PARAM]: String(id), [MANAGER_STATUS_PARAM]: undefined };
  return { [MANAGER_STATUS_PARAM]: undefined, [MANAGER_STATUS_ID_PARAM]: undefined };
}

/** Positive integer or `undefined` — a junk value is a URL typo, not an api error. */
function parseIntParam(value: string | undefined): number | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

// ---------------------------------------------------------------------------
// The optimistic half of #5.6 — what the cache shows between commit and answer
// ---------------------------------------------------------------------------

/**
 * The journal as the replace-set leaves it, before the PUT answers: the chosen ids
 * become the ACTIVE rows (a row the cache already knew keeps its label, its actor and
 * its `assignedAt`; a brand-new one borrows the dictionary's label and carries a
 * placeholder actor), and every other active row closes with the same instant. The
 * closed history is carried over untouched — the journal is never deleted (Р9).
 *
 * Pure and framework-free, so the unit suite pins it without a DOM; the api module
 * imports it for `useSetApplicationManagerStatuses`. `assignedByUserId: 0` on a
 * synthesized row is a deliberate placeholder — nothing renders the actor this wave,
 * and the PUT's own answer replaces the whole array anyway.
 */
export function optimisticJournal(
  current: readonly ApplicationManagerStatusDto[] | undefined,
  statusIds: readonly number[],
  labels: ReadonlyMap<number, string> = new Map(),
  nowIso: string = new Date().toISOString(),
): ApplicationManagerStatusDto[] {
  const closed = (current ?? []).filter((row) => row.removedAt !== null);
  const previous = new Map(
    (current ?? []).filter((row) => row.removedAt === null).map((row) => [row.statusId, row]),
  );

  const active = statusIds.map((statusId) => {
    const known = previous.get(statusId);
    if (known) return { ...known };
    return {
      statusId,
      label: labels.get(statusId) ?? '',
      assignedAt: nowIso,
      assignedByUserId: 0,
      removedAt: null,
      removedByUserId: null,
    } satisfies ApplicationManagerStatusDto;
  });

  const closedNow = [...previous.keys()]
    .filter((statusId) => !statusIds.includes(statusId))
    .map((statusId) => {
      const row = previous.get(statusId) as ApplicationManagerStatusDto;
      return { ...row, removedAt: nowIso, removedByUserId: 0 };
    });

  // Active first (newest assignment leads — the D8 display rule reads the head), then history.
  return [...active, ...closedNow, ...closed];
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.8.2, Р9/#3, T.Z17) — the register's live «Статус» cell
// ---------------------------------------------------------------------------

/**
 * The second line of the register's «Статус» cell: the #3 row's resolved
 * `managerStatus`, printed as a smaller line under the system status. `null` (no
 * active journal row, the key absent, or a blank the api would never send but a
 * hand-built stub might) renders NOTHING — the cell keeps the pre-1.8.2 layout, so
 * an unassigned row never reads «без статуса» twice per screen; the profile block
 * owns that wording.
 *
 * Pure, so the unit suite pins the set / null pair without a DOM.
 */
export function managerStatusCellLine(managerStatus: string | null | undefined): string | null {
  if (typeof managerStatus !== 'string') return null;
  const trimmed = managerStatus.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * The React Query key prefixes one #5.6 commit invalidates, in the order the api
 * module's `onSettled` applies them. The journal (#5.5) and the #4 detail are the
 * T.Y10 pair; the register root is the T.Z17 BRIDGE — `ApplicationDto` carries the
 * resolved label on every row since 1.8.2, so a page already on screen would keep
 * showing the old «Статус» cell forever without it.
 *
 * Prefix matching does the rest: `['applications', 'list']` reaches EVERY loaded
 * slice, including one currently filtered by a manager status, whose refetch
 * re-applies `?managerStatusId=` / `?managerStatus=none` server-side — the row
 * leaves or re-enters the visible set with no manual reload and with no URL write
 * (the stale-snapshot double-write T.Y10 fixed stays fixed: invalidation never
 * touches the params). Pure, so the unit suite pins that a refactor cannot drop the
 * bridge silently.
 */
export function managerStatusWriteInvalidationKeys(pguId: string): ReadonlyArray<readonly string[]> {
  return [
    ['manager-statuses', 'application', pguId], // #5.5 — the drawer's own journal cache
    ['applications', 'detail', pguId], // #4 — the card's additive copy of it
    ['applications', 'list'], // #3 — every loaded register page (the bridge)
  ];
}

/**
 * The membership rule a manager-status filter applies to one row — the mirror of #3's
 * own `where` (`some: { statusId, removedAt: null }` for the id bucket, `none:
 * { removedAt: null }` for the «без статуса» bucket). The api stays the authority:
 * the register never filters rows client-side. This pins the rule the e2e stub
 * emulates, so a stub that drifts from the api is caught by the same suite that
 * pins the bridge.
 */
export function matchesManagerStatusFilter(
  filter: ManagerStatusFilter | undefined,
  /** The journal winner's dictionary id, `null` when the row has no active row. */
  activeStatusId: number | null,
): boolean {
  if (filter === undefined) return true;
  if (filter.kind === 'none') return activeStatusId === null;
  return activeStatusId === filter.id;
}

// ---------------------------------------------------------------------------
// Error mapping (the api's own vocabulary, RU)
// ---------------------------------------------------------------------------

export interface ManagerStatusFailureShape {
  status?: number;
  code?: string;
  message?: string;
}

/** The 400 #5.6 answers for an unknown or inactive dictionary id. */
export const MANAGER_STATUS_INVALID_TEXT =
  'Этот статус нельзя назначить — он удалён из словаря или не существует. Обновите список.';

/** What a failed write prints; the 400 is the one outcome the manager can act on. */
export function managerStatusErrorText(
  error: ManagerStatusFailureShape | null | undefined,
  fallback = 'Не удалось сохранить статус менеджера.',
): string | null {
  if (!error) return null;
  if (error.code === 'VALIDATION' || error.status === 400) return MANAGER_STATUS_INVALID_TEXT;
  if (error.code === 'FORBIDDEN' || error.status === 403) {
    return 'Недостаточно прав: статусы менеджера назначают роли не ниже менеджера.';
  }
  if (error.code === 'NOT_FOUND' || error.status === 404) return 'Заявление не найдено.';
  const message = error.message?.trim();
  return message ? message : fallback;
}
